// ContractorMaster.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { PPC_API_URL } from './PPCContext';

// ── Constants ─────────────────────────────────────────────────────────────────
const ALL_MODULES = [
  { key: 'BRAVO', label: '🔴 Bravo',  sub: 'H.ON Processing' },
  { key: 'PAPA',  label: '🟢 Papa',   sub: 'Peeling Unit'    },
  { key: 'ROMEO', label: '🔵 Romeo',  sub: 'Packing Line'    },
  { key: 'GOLF',  label: '🟣 Golf',   sub: 'Grading Unit'    },
];

const AVATAR_COLORS = [
  '#d32f2f','#1565c0','#059669','#d97706','#7b1fa2',
  '#0097a7','#e64a19','#388e3c','#1976d2','#c62828',
];

const moduleBadgeStyle = {
  BRAVO: { background: '#fff3e0', color: '#e65100' },
  PAPA:  { background: '#e8f5e9', color: '#1b5e20' },
  ROMEO: { background: '#e3f2fd', color: '#0d47a1' },
  GOLF:  { background: '#f3e5f5', color: '#4a148c' },
};

const emptyForm = () => ({
  name: '', code: '', contact_person: '', phone: '',
  email: '', location: '', address: '', notes: '',
  modules: ['BRAVO'], is_active: true,
});

const initials = (name) =>
  (name || '?').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();

const avatarColor = (idx) => AVATAR_COLORS[idx % AVATAR_COLORS.length];

const PER_PAGE = 10;

// ── Component ─────────────────────────────────────────────────────────────────
const ContractorMaster = () => {
  const navigate   = useNavigate();
  const refreshShared = () => {}; // no-op — shared context not used

  // Try to get user role — works with or without PPCAuth
  let isAdmin = true;
  try {
    const token = localStorage.getItem('ppc_token') || localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      isAdmin = payload?.role === 'admin' || payload?.is_admin === true;
    }
  } catch (_) { isAdmin = true; }

  // ── State ──────────────────────────────────────────────────────────────────
  const [contractors, setContractors] = useState([]);
  const [allContractors, setAllContractors] = useState([]); // unfiltered — used for duplicate check
  const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0, all_modules: 0, bravo_only: 0 });
  const [loading, setLoading] = useState(false);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [toast, setToast] = useState(null);

  // Filters / pagination
  const [search, setSearch] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });

  // Form modal
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Delete modal
  const [showDelete, setShowDelete] = useState(false);
  const [deletingRow, setDeletingRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Auth header ─────────────────────────────────────────────────────────────
  const getAuth = () => {
    const token = localStorage.getItem('ppc_token') || localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  // ── Toast ───────────────────────────────────────────────────────────────────
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3200);
  };

  // ── Local state helpers (used when API is unavailable) ─────────────────────
  const computeSummary = (list) => ({
    total:       list.length,
    active:      list.filter(c => c.is_active).length,
    inactive:    list.filter(c => !c.is_active).length,
    all_modules: list.filter(c => Array.isArray(c.modules) && c.modules.length === 4).length,
    bravo_only:  list.filter(c => Array.isArray(c.modules) && c.modules.length === 1 && c.modules[0] === 'BRAVO').length,
  });

  const applyLocalFilters = useCallback((list, q, mod, status, pg) => {
    let filtered = [...list];
    if (q) {
      const lq = q.toLowerCase();
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(lq) ||
        c.code.toLowerCase().includes(lq) ||
        (c.contact_person || '').toLowerCase().includes(lq)
      );
    }
    if (mod)    filtered = filtered.filter(c => Array.isArray(c.modules) && c.modules.includes(mod));
    if (status) filtered = filtered.filter(c => status === 'active' ? c.is_active : !c.is_active);
    const total = filtered.length;
    const pages = Math.max(1, Math.ceil(total / PER_PAGE));
    const safePg = Math.min(pg, pages);
    const start  = (safePg - 1) * PER_PAGE;
    return { items: filtered.slice(start, start + PER_PAGE), pagination: { total, pages } };
  }, []);

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchContractors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ action: 'list', page, limit: PER_PAGE });
      if (search)       params.set('q',      search);
      if (filterModule) params.set('module', filterModule);
      if (filterStatus) params.set('status', filterStatus);

      const res = await axios.get(`${PPC_API_URL}/contractors.php?${params}`, getAuth());

      if (res.data?.success) {
        setContractors(res.data.contractors || []);
        setPagination(res.data.pagination   || { total: 0, pages: 1 });
        setSummary(res.data.summary         || computeSummary(res.data.contractors || []));
        setApiAvailable(true);

        // Also fetch unfiltered for duplicate check
        const allRes = await axios.get(
          `${PPC_API_URL}/contractors.php?action=list&limit=1000`,
          getAuth()
        );
        if (allRes.data?.success) setAllContractors(allRes.data.contractors || []);
      }
    } catch (err) {
      setApiAvailable(false);
      // Load from cache
      try {
        const cached = JSON.parse(localStorage.getItem('ppc_contractors_cache') || 'null');
        if (cached?.data?.length) {
          setAllContractors(cached.data);
          const { items, pagination: pg } = applyLocalFilters(cached.data, search, filterModule, filterStatus, page);
          setContractors(items);
          setPagination(pg);
          setSummary(computeSummary(cached.data));
          showToast('Working offline — showing cached contractor data', 'warning');
          return;
        }
      } catch (_) {}
      showToast(err.response?.data?.error || 'Failed to load contractors', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterModule, filterStatus, applyLocalFilters]);

  useEffect(() => { fetchContractors(); }, [fetchContractors]);
  useEffect(() => { setPage(1); }, [search, filterModule, filterStatus]);

  // ── Form helpers ─────────────────────────────────────────────────────────────
  const setField = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    setFormErrors(prev => ({ ...prev, [key]: '' }));
  };

  const toggleModule = (mod) => {
    setForm(prev => ({
      ...prev,
      modules: prev.modules.includes(mod)
        ? prev.modules.filter(m => m !== mod)
        : [...prev.modules, mod],
    }));
    setFormErrors(prev => ({ ...prev, modules: '' }));
  };

  // ── FIXED: Duplicate validation ──────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    const trimName = form.name.trim().toUpperCase();
    const trimCode = form.code.trim().toUpperCase();

    if (!trimName)             errors.name    = 'Contractor name is required';
    if (!trimCode)             errors.code    = 'Contractor code is required';
    if (form.modules.length === 0) errors.modules = 'Select at least one module';

    // Phone validation (optional but if filled must be valid)
    if (form.phone && !/^[\d\s\+\-\(\)]{7,15}$/.test(form.phone.trim())) {
      errors.phone = 'Enter a valid phone number';
    }
    // Email validation
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = 'Enter a valid email address';
    }

    // Duplicate name check
    if (trimName) {
      const duplicate = allContractors.find(c =>
        c.name.toUpperCase() === trimName && c.id !== editingId
      );
      if (duplicate) errors.name = `Contractor "${trimName}" already exists`;
    }

    // Duplicate code check
    if (trimCode) {
      const dupCode = allContractors.find(c =>
        c.code.toUpperCase() === trimCode && c.id !== editingId
      );
      if (dupCode) errors.code = `Code "${trimCode}" is already used by ${dupCode.name}`;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── ADD ──────────────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormErrors({});
    setShowForm(true);
  };

  // ── EDIT ─────────────────────────────────────────────────────────────────────
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name:           c.name           || '',
      code:           c.code           || '',
      contact_person: c.contact_person || '',
      phone:          c.phone          || '',
      email:          c.email          || '',
      location:       c.location       || '',
      address:        c.address        || '',
      notes:          c.notes          || '',
      modules:        Array.isArray(c.modules) ? [...c.modules] : ['BRAVO'],
      is_active:      typeof c.is_active === 'boolean' ? c.is_active : true,
    });
    setFormErrors({});
    setShowForm(true);
  };

  // ── SAVE ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);

    const payload = {
      ...form,
      name: form.name.trim().toUpperCase(),
      code: form.code.trim().toUpperCase(),
    };

    // ── API save ────────────────────────────────────────────────────────────
    if (apiAvailable) {
      try {
        let res;
        if (editingId) {
          payload.id = editingId;
          res = await axios.put(`${PPC_API_URL}/contractors.php?action=update`, payload, getAuth());
        } else {
          res = await axios.post(`${PPC_API_URL}/contractors.php?action=create`, payload, getAuth());
        }

        if (res.data?.success) {
          showToast(res.data.message || (editingId ? 'Contractor updated!' : 'Contractor added!'));
          setShowForm(false);
          fetchContractors();
          refreshShared(); // update shared dropdown lists
          return;
        } else {
          // Server returned success:false — show message (e.g. duplicate from server side)
          const errField = res.data?.field;
          if (errField) setFormErrors(prev => ({ ...prev, [errField]: res.data.error }));
          else showToast(res.data?.error || 'Save failed', 'error');
          setSaving(false);
          return;
        }
      } catch (err) {
        const serverError = err.response?.data?.error || '';
        // Detect server-side duplicate errors and show inline
        if (serverError.toLowerCase().includes('duplicate') || serverError.toLowerCase().includes('already exists')) {
          if (serverError.toLowerCase().includes('name')) {
            setFormErrors(prev => ({ ...prev, name: serverError }));
          } else if (serverError.toLowerCase().includes('code')) {
            setFormErrors(prev => ({ ...prev, code: serverError }));
          } else {
            showToast(serverError, 'error');
          }
        } else {
          showToast(serverError || 'Failed to save contractor', 'error');
        }
        setSaving(false);
        return;
      }
    }

    // ── Offline / local state save ──────────────────────────────────────────
    try {
      const cached = JSON.parse(localStorage.getItem('ppc_contractors_cache') || '{"data":[]}');
      const list = cached.data || [];

      if (editingId) {
        const idx = list.findIndex(c => c.id === editingId);
        if (idx !== -1) list[idx] = { ...list[idx], ...payload };
        showToast('Contractor updated (offline mode)');
      } else {
        const maxId = list.reduce((m, c) => Math.max(m, c.id || 0), 0);
        list.push({ id: maxId + 1, ...payload });
        showToast('Contractor added (offline mode)');
      }

      localStorage.setItem('ppc_contractors_cache', JSON.stringify({ data: list, ts: Date.now() }));
      setAllContractors(list);
      const { items, pagination: pg } = applyLocalFilters(list, search, filterModule, filterStatus, page);
      setContractors(items);
      setPagination(pg);
      setSummary(computeSummary(list));
      refreshShared();
      setShowForm(false);
    } catch (_) {
      showToast('Failed to save contractor', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── DELETE ───────────────────────────────────────────────────────────────────
  const openDelete = (c) => { setDeletingRow(c); setShowDelete(true); };

  const handleDelete = async () => {
    if (!deletingRow) return;
    setDeleting(true);

    if (apiAvailable) {
      try {
        const res = await axios.delete(
          `${PPC_API_URL}/contractors.php?action=delete&id=${deletingRow.id}&hard=1`,
          getAuth()
        );
        if (res.data?.success) {
          showToast(res.data.message || 'Contractor deleted');
          setShowDelete(false);
          setDeletingRow(null);
          fetchContractors();
          refreshShared();
          return;
        }
        showToast(res.data?.error || 'Delete failed', 'error');
      } catch (err) {
        showToast(err.response?.data?.error || 'Delete failed', 'error');
      } finally {
        setDeleting(false);
      }
      return;
    }

    // Offline delete
    try {
      const cached = JSON.parse(localStorage.getItem('ppc_contractors_cache') || '{"data":[]}');
      const list   = (cached.data || []).filter(c => c.id !== deletingRow.id);
      localStorage.setItem('ppc_contractors_cache', JSON.stringify({ data: list, ts: Date.now() }));
      setAllContractors(list);
      const { items, pagination: pg } = applyLocalFilters(list, search, filterModule, filterStatus, page);
      setContractors(items);
      setPagination(pg);
      setSummary(computeSummary(list));
      refreshShared();
      showToast('Contractor deleted (offline mode)');
      setShowDelete(false);
      setDeletingRow(null);
    } catch (_) {
      showToast('Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── TOGGLE STATUS ─────────────────────────────────────────────────────────────
  const handleToggleStatus = async (c) => {
    if (apiAvailable) {
      try {
        const res = await axios.put(
          `${PPC_API_URL}/contractors.php?action=toggle-status`,
          { id: c.id },
          getAuth()
        );
        if (res.data?.success) {
          showToast(res.data.message || 'Status updated');
          fetchContractors();
          refreshShared();
          return;
        }
      } catch (err) {
        showToast(err.response?.data?.error || 'Failed to update status', 'error');
        return;
      }
    }
    // Offline toggle
    const cached = JSON.parse(localStorage.getItem('ppc_contractors_cache') || '{"data":[]}');
    const list   = (cached.data || []).map(x => x.id === c.id ? { ...x, is_active: !x.is_active } : x);
    localStorage.setItem('ppc_contractors_cache', JSON.stringify({ data: list, ts: Date.now() }));
    setAllContractors(list);
    const { items, pagination: pg } = applyLocalFilters(list, search, filterModule, filterStatus, page);
    setContractors(items);
    setSummary(computeSummary(list));
    refreshShared();
    showToast(`Contractor ${c.is_active ? 'deactivated' : 'activated'} (offline)`);
  };

  // ── EXPORT ───────────────────────────────────────────────────────────────────
  const handleExport = () => {
    const headers = ['Name','Code','Contact Person','Phone','Email','Location','Modules','Status','Notes'];
    const data = contractors.map(c => [
      c.name, c.code,
      c.contact_person || '', c.phone || '', c.email || '', c.location || '',
      (Array.isArray(c.modules) ? c.modules : []).join(', '),
      c.is_active ? 'Active' : 'Inactive',
      c.notes || '',
    ]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([['PPC — Contractor Master'], [], headers, ...data]);
    ws['!cols'] = headers.map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Contractors');
    XLSX.writeFile(wb, `PPC_Contractors_${new Date().toISOString().slice(0,10)}.xlsx`);
    showToast('Exported successfully');
  };

  // ── RENDER ────────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard">
      {/* ── Header ── */}
      <header className="dashboard-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/ppc')}>← Back</button>
          <h1 className="dashboard-title bravo">👷 CONTRACTOR MASTER</h1>
          <span className="user-role-badge">PPC Module</span>
          {!apiAvailable && (
            <span style={{ fontSize: '10px', background: '#fef3c7', color: '#92400e', padding: '3px 9px', borderRadius: '12px', fontWeight: 700 }}>
              ⚠️ Offline Mode
            </span>
          )}
        </div>
        <div className="header-right">
          <span className="user-role-badge" style={{ background: 'rgba(255,255,255,.28)' }}>
            {isAdmin ? '👑 Admin' : '👤 User'}
          </span>
        </div>
      </header>

      <main className="dashboard-content">
        {/* ── Stat Cards ── */}
        <section className="top-summary-cards" style={{ marginBottom: '14px' }}>
          {[
            { label: 'Total Contractors', value: summary.total,       icon: '👷', color: '#1565c0' },
            { label: 'Active',            value: summary.active,      icon: '✅', color: '#059669' },
            { label: 'Inactive',          value: summary.inactive,    icon: '⛔', color: '#9ca3af' },
            { label: 'All 4 Modules',     value: summary.all_modules, icon: '🔄', color: '#d97706' },
            { label: 'Bravo Only',        value: summary.bravo_only,  icon: '📋', color: '#1565c0' },
          ].map((s, i) => (
            <div key={i} className="summary-stat-card" style={{ borderLeft: `4px solid ${s.color}` }}>
              <div className="stat-icon">{s.icon}</div>
              <div className="stat-content">
                <div className="stat-label">{s.label}</div>
                <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
              </div>
            </div>
          ))}
        </section>

        {/* ── Toolbar ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flex: 1 }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: 1, maxWidth: '320px' }}>
              <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '14px', pointerEvents: 'none' }}>🔍</span>
              <input
                type="text"
                placeholder="Search name, code, contact..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #e2e6ea', borderRadius: '8px', fontFamily: 'inherit', fontSize: '12px', background: '#fff', outline: 'none' }}
              />
            </div>
            <select value={filterModule} onChange={e => setFilterModule(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #e2e6ea', borderRadius: '8px', fontFamily: 'inherit', fontSize: '12px', background: '#fff', minWidth: '140px', cursor: 'pointer' }}>
              <option value="">All Modules</option>
              {ALL_MODULES.map(m => <option key={m.key} value={m.key}>{m.key}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #e2e6ea', borderRadius: '8px', fontFamily: 'inherit', fontSize: '12px', background: '#fff', minWidth: '130px', cursor: 'pointer' }}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleExport}
              style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#d32f2f,#1565c0)', color: '#fff', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
              📥 Export
            </button>
            {isAdmin && (
              <button onClick={openAdd}
                style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', background: '#059669', color: '#fff', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                ＋ Add Contractor
              </button>
            )}
          </div>
        </div>

        {/* ── Table Card ── */}
        <section className="summary-card">
          <div className="summary-header">
            <h3 style={{ fontSize: '14px', fontWeight: '600', margin: 0, color: '#333' }}>Contractor List</h3>
            <span style={{ fontSize: '11px', color: '#6b7280', background: '#f0f2f5', padding: '3px 9px', borderRadius: '20px' }}>
              {pagination.total} record{pagination.total !== 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
              <div style={{ fontSize: '32px', marginBottom: '10px', opacity: .5 }}>⏳</div>
              Loading contractors...
            </div>
          ) : contractors.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <div style={{ fontSize: '44px', marginBottom: '12px', opacity: .5 }}>🔍</div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#6b7280', marginBottom: '6px' }}>No contractors found</div>
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                {search || filterModule || filterStatus
                  ? 'Try adjusting your search or filter'
                  : isAdmin ? 'Click "+ Add Contractor" to add the first one' : 'No contractors have been added yet'}
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="summary-table" style={{ fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '9px 12px', width: '36px' }}>#</th>
                    <th style={{ textAlign: 'left', padding: '9px 12px', minWidth: '160px' }}>Contractor</th>
                    <th style={{ textAlign: 'left', padding: '9px 12px' }}>Code</th>
                    <th style={{ textAlign: 'left', padding: '9px 12px' }}>Contact Person</th>
                    <th style={{ textAlign: 'left', padding: '9px 12px' }}>Phone</th>
                    <th style={{ textAlign: 'center', padding: '9px 12px', minWidth: '200px' }}>Modules</th>
                    <th style={{ textAlign: 'center', padding: '9px 12px' }}>Status</th>
                    {isAdmin && <th style={{ textAlign: 'center', padding: '9px 12px', minWidth: '130px' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {contractors.map((c, idx) => (
                    <tr key={c.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '10px 12px', color: '#9ca3af', fontSize: '11px', textAlign: 'center' }}>
                        {(page - 1) * PER_PAGE + idx + 1}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '34px', height: '34px', borderRadius: '50%', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                            fontWeight: '700', color: '#fff', flexShrink: 0,
                            background: avatarColor(idx),
                          }}>
                            {initials(c.name)}
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '12px' }}>{c.name}</div>
                            <div style={{ fontSize: '10px', color: '#9ca3af', fontFamily: 'monospace' }}>{c.location || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: '700', color: '#1565c0' }}>{c.code}</span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#374151' }}>{c.contact_person || '—'}</td>
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '11px' }}>{c.phone || '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                          {Array.isArray(c.modules) && c.modules.length === 4 ? (
                            <span style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: '700', background: 'rgba(211,47,47,.1)', color: '#374151' }}>
                              ALL MODULES
                            </span>
                          ) : (
                            (Array.isArray(c.modules) ? c.modules : []).map(m => (
                              <span key={m} style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: '700', ...(moduleBadgeStyle[m] || {}) }}>
                                {m}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {isAdmin ? (
                          <button
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: '500', color: c.is_active ? '#059669' : '#9ca3af', background: 'transparent', border: 'none', cursor: 'pointer', padding: '3px 8px', borderRadius: '12px' }}
                            title={c.is_active ? 'Click to deactivate' : 'Click to activate'}
                            onClick={() => handleToggleStatus(c)}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0, display: 'inline-block', background: c.is_active ? '#059669' : '#9ca3af' }} />
                            {c.is_active ? 'Active' : 'Inactive'}
                          </button>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: '500', color: c.is_active ? '#059669' : '#9ca3af' }}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: c.is_active ? '#059669' : '#9ca3af', display: 'inline-block' }} />
                            {c.is_active ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      {isAdmin && (
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                            <button onClick={() => openEdit(c)}
                              style={{ padding: '4px 11px', border: 'none', borderRadius: '5px', background: '#1565c0', color: '#fff', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>
                              EDIT
                            </button>
                            <button onClick={() => openDelete(c)}
                              style={{ padding: '4px 11px', border: 'none', borderRadius: '5px', background: '#d32f2f', color: '#fff', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>
                              DELETE
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid #e5e7eb', background: '#fafbfc', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontSize: '11px', color: '#6b7280' }}>
              {pagination.total === 0 ? 'No records' :
                `Showing ${Math.min((page-1)*PER_PAGE+1, pagination.total)}–${Math.min(page*PER_PAGE, pagination.total)} of ${pagination.total}`}
            </span>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}
                style={{ padding: '5px 11px', border: '1px solid #e2e6ea', borderRadius: '6px', background: '#fff', fontSize: '11px', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? .4 : 1 }}>
                ‹ Prev
              </button>
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                const pNum = page <= 3 ? i + 1 : page - 2 + i;
                if (pNum < 1 || pNum > pagination.pages) return null;
                return (
                  <button key={pNum} onClick={() => setPage(pNum)}
                    style={{ padding: '5px 11px', border: '1px solid #e2e6ea', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', background: pNum === page ? '#1565c0' : '#fff', color: pNum === page ? '#fff' : '#374151', borderColor: pNum === page ? '#1565c0' : '#e2e6ea' }}>
                    {pNum}
                  </button>
                );
              })}
              <button onClick={() => setPage(p => p + 1)} disabled={page >= pagination.pages}
                style={{ padding: '5px 11px', border: '1px solid #e2e6ea', borderRadius: '6px', background: '#fff', fontSize: '11px', cursor: page >= pagination.pages ? 'not-allowed' : 'pointer', opacity: page >= pagination.pages ? .4 : 1 }}>
                Next ›
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* ════ ADD / EDIT MODAL ════ */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '600px', maxHeight: '93vh', overflowY: 'auto' }}>

            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg,#d32f2f 0%,#1565c0 100%)', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, borderRadius: '16px 16px 0 0', zIndex: 10 }}>
              <div>
                <div style={{ color: '#fff', fontSize: '15px', fontWeight: '600' }}>
                  {editingId ? '✏️ Edit Contractor' : '➕ Add Contractor'}
                </div>
                <div style={{ color: 'rgba(255,255,255,.75)', fontSize: '11px', marginTop: '3px' }}>
                  {editingId ? form.name : 'Fill in the contractor details below'}
                </div>
              </div>
              <button onClick={() => setShowForm(false)} style={{ width: '28px', height: '28px', borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,.2)', color: '#fff', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            <div style={{ padding: '20px', display: 'grid', gap: '16px' }}>
              <SectionDivider label="Basic Information" />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <FormField label="Contractor Name *" error={formErrors.name}>
                  <input value={form.name} onChange={e => setField('name', e.target.value.toUpperCase())}
                    placeholder="e.g. DBS CONTRACTORS" style={inputStyle(formErrors.name)} />
                </FormField>
                <FormField label="Contractor Code *" error={formErrors.code}>
                  <input value={form.code} onChange={e => setField('code', e.target.value.toUpperCase())}
                    placeholder="e.g. DBS" style={inputStyle(formErrors.code)} maxLength={20} />
                </FormField>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <FormField label="Contact Person">
                  <input value={form.contact_person} onChange={e => setField('contact_person', e.target.value)}
                    placeholder="Full name" style={inputStyle()} />
                </FormField>
                <FormField label="Phone Number" error={formErrors.phone}>
                  <input value={form.phone} onChange={e => setField('phone', e.target.value)}
                    placeholder="+91 00000 00000" style={inputStyle(formErrors.phone)} />
                </FormField>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <FormField label="Email" error={formErrors.email}>
                  <input type="email" value={form.email} onChange={e => setField('email', e.target.value)}
                    placeholder="email@example.com" style={inputStyle(formErrors.email)} />
                </FormField>
                <FormField label="Location / City">
                  <input value={form.location} onChange={e => setField('location', e.target.value)}
                    placeholder="City or area" style={inputStyle()} />
                </FormField>
              </div>

              <FormField label="Address">
                <textarea value={form.address} onChange={e => setField('address', e.target.value)}
                  placeholder="Full address (optional)" rows={2}
                  style={{ ...inputStyle(), resize: 'vertical', fontFamily: 'inherit' }} />
              </FormField>

              <SectionDivider label="Module Assignment" />
              {formErrors.modules && <span style={{ color: '#d32f2f', fontSize: '11px', marginTop: '-8px' }}>{formErrors.modules}</span>}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px' }}>
                {ALL_MODULES.map(m => {
                  const checked = form.modules.includes(m.key);
                  return (
                    <div key={m.key} onClick={() => toggleModule(m.key)}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', border: `1.5px solid ${checked ? '#1565c0' : '#e2e6ea'}`, borderRadius: '8px', cursor: 'pointer', background: checked ? '#dbeafe' : '#fff', transition: 'all .18s' }}>
                      <input type="checkbox" checked={checked} readOnly style={{ width: '15px', height: '15px', accentColor: '#1565c0', cursor: 'pointer', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: checked ? '#1565c0' : '#374151' }}>{m.label}</div>
                        <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '1px' }}>{m.sub}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <SectionDivider label="Status" />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e2e6ea' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>Contractor Active</div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                    {form.is_active
                      ? 'Contractor will appear in all assigned dashboards'
                      : 'Contractor will be hidden from all dashboards'}
                  </div>
                </div>
                <label style={{ position: 'relative', width: '44px', height: '24px', flexShrink: 0, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setField('is_active', e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                  <span style={{ position: 'absolute', inset: 0, borderRadius: '24px', cursor: 'pointer', transition: 'background .22s', background: form.is_active ? '#059669' : '#d1d5db' }}>
                    <span style={{ position: 'absolute', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', top: '3px', left: form.is_active ? '23px' : '3px', transition: 'left .22s', boxShadow: '0 1px 4px rgba(0,0,0,.2)' }} />
                  </span>
                </label>
              </div>

              <FormField label="Notes / Remarks">
                <textarea value={form.notes} onChange={e => setField('notes', e.target.value)}
                  placeholder="Any additional notes..." rows={2}
                  style={{ ...inputStyle(), resize: 'vertical', fontFamily: 'inherit' }} />
              </FormField>
            </div>

            <div style={{ padding: '0 20px 20px', display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowForm(false)}
                style={{ flex: 1, padding: '11px', borderRadius: '8px', border: '1.5px solid #e2e6ea', background: '#fff', fontFamily: 'inherit', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 2, padding: '11px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#d32f2f 0%,#1565c0 100%)', color: '#fff', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? .6 : 1 }}>
                {saving ? 'Saving...' : editingId ? 'Update Contractor' : 'Save Contractor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════ DELETE MODAL ════ */}
      {showDelete && deletingRow && (
        <div className="modal-overlay" onClick={() => setShowDelete(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '30px 24px', width: '100%', maxWidth: '380px', textAlign: 'center' }}>
            <div style={{ fontSize: '44px', marginBottom: '14px' }}>🗑️</div>
            <div style={{ fontSize: '17px', fontWeight: '700', color: '#d32f2f', marginBottom: '6px' }}>Delete Contractor?</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>You are about to permanently delete</div>
            <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '14px' }}>
              {deletingRow.name} ({deletingRow.code})
            </div>
            <div style={{ fontSize: '11px', color: '#d32f2f', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: '7px', padding: '9px 12px', marginBottom: '18px', textAlign: 'left' }}>
              ⚠️ This will remove the contractor from ALL PPC module dashboards. Existing entry records will not be affected.
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowDelete(false)}
                style={{ flex: 1, padding: '11px', borderRadius: '8px', border: '1.5px solid #e2e6ea', background: '#fff', fontFamily: 'inherit', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ flex: 2, padding: '11px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#dc2626,#ef4444)', color: '#fff', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700', cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? .6 : 1 }}>
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={`toast ${toast.type}`}>{toast.message}</div>
      )}
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────
const SectionDivider = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
    <span style={{ flex: 1, height: '1px', background: '#e2e6ea' }} />
    <span style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px', whiteSpace: 'nowrap' }}>{label}</span>
    <span style={{ flex: 1, height: '1px', background: '#e2e6ea' }} />
  </div>
);

const FormField = ({ label, error, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: '#6b7280', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</label>
    {children}
    {error && <span style={{ fontSize: '10px', color: '#d32f2f', marginTop: '3px', display: 'block' }}>{error}</span>}
  </div>
);

const inputStyle = (error) => ({
  width: '100%', padding: '9px 11px', borderRadius: '7px',
  border: `1.5px solid ${error ? '#d32f2f' : '#e2e6ea'}`,
  fontFamily: 'inherit', fontSize: '13px', background: '#fff', outline: 'none',
  boxSizing: 'border-box',
});

export default ContractorMaster;
